// 複習中心 — 把已實作但無入口的複習工具集中起來的學習端 hub。
//
// 背景:`runReviewPaper`(生複習考卷)只藏在錯題本內,`runVocabInput`(英文
// 單字 / 聽寫)完全沒有使用者入口(孤兒)。這個 hub 把它們集中成三張大卡,
// 並由 main.ts 接進首頁導覽。
//
// 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 紙片卡
// `academicCard`(boss 2026-06-08;見 wiki page-470f59c0)。不再各畫各的。
// 學習介面要專業(boss 2026-05-23):純文字、無音效、無 mascot、無彩光、文案中性。
// 學習/遊戲分離鐵則:只走學習側(錯題 / wiki / 單字),絕不碰戰鬥數值。

import { mountEscapeButton } from '../escape-button';
import { pageShell, academicCard } from '../ui/components';
import { runReviewPaper } from './review-paper';
import { runVocabInput } from './vocab-input';
import { runGradeReview } from './grade-review';
import { runListeningExamLaunch } from './listening-exam-launch';
import { runMathExamLaunch } from './math-exam-launch';
import { runExamCenter } from './exam-center';
import { openExamPlanner } from '../exam-plan/exam-review';

/** 一張複習卡的設定。`id` 是測試 / wiring 依賴的 DOM id,不可改。 */
interface ReviewCardSpec {
  id: string;
  icon: string;
  label: string;
  sub: string;
  run: (container: HTMLElement, onDone: () => void) => void;
}

/**
 * Render 複習中心 into `container`.
 * onBack: called when the user exits (✕ 離開 button).
 */
export function renderReviewHub(container: HTMLElement, onBack: () => void): void {
  container.innerHTML = '';

  // pageShell 不帶 onBack;自己掛 escape 鈕以握住 unmount handle,導頁到子畫面前
  // 先卸載,避免堆疊兩顆「✕ 離開」(沿用全站 mountEscapeButton 慣例)。
  const shell = pageShell(container, {
    title: '複習中心',
    subtitle: '把學過的東西再練一次,把錯題變成練習。',
  });

  // 既有測試斷言 `.review-hub-title`(含「複習中心」)/ `.review-hub-sub` 仍在。
  // pageShell 的標題 = `.ef-title`、副標 = `.ef-subtitle`;補掛 legacy class 相容,外觀不變。
  shell.root.querySelector('.ef-title')?.classList.add('review-hub-title');
  shell.root.querySelector('.ef-subtitle')?.classList.add('review-hub-sub');

  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    onBack();
  });

  const root = document.createElement('div');
  root.className = 'review-hub';
  shell.body.appendChild(root);

  const cards = document.createElement('div');
  cards.className = 'review-hub-cards';
  cards.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  root.appendChild(cards);

  const specs: ReviewCardSpec[] = [
    {
      id: 'review-hub-exam-center',
      icon: '📝',
      label: '出考卷中心',
      sub: '選科目 + 章節/單元/課 → 產生對應範圍的考卷(程式產生或題庫組卷)',
      run: (c, done) => runExamCenter(c, done),
    },
    {
      id: 'review-hub-exam-plan',
      icon: '🗓️',
      label: '考試規劃',
      sub: '填考試日期 → 選某次考 → 撈範圍內錯題生複習卷',
      run: (c, done) => openExamPlanner(c, done),
    },
    {
      id: 'review-hub-paper',
      icon: '📄',
      label: '生成複習考卷',
      sub: '把錯題變成一張雙面練習卷',
      run: (c, done) => void runReviewPaper(c, done),
    },
    {
      id: 'review-hub-vocab',
      icon: '🎧',
      label: '英文單字 / 聽寫',
      sub: '輸入單字、用聽寫練拼字',
      run: (c, done) => runVocabInput(c, done),
    },
    {
      id: 'review-hub-listening',
      icon: '🔊',
      label: '英語聽力卷',
      sub: '聽發音作答的段考型聽力卷,可重抽新卷或列印',
      run: (c, done) => runListeningExamLaunch(c, done),
    },
    {
      id: 'review-hub-math-exam',
      icon: '🧮',
      label: '數學考卷',
      sub: '參考段考真卷題型自動出整張數學卷,可重抽新卷或列印',
      run: (c, done) => runMathExamLaunch(c, done),
    },
    {
      id: 'review-hub-grade',
      icon: '✅',
      label: '批改複習考卷(拍照)',
      sub: '拍下寫完的考卷,自動批改、錯題回流',
      run: (c, done) => runGradeReview(c, done),
    },
  ];

  for (const spec of specs) {
    const card = academicCard({
      icon: spec.icon,
      label: spec.label,
      sub: spec.sub,
      onClick: () => {
        unmountEscape();
        container.innerHTML = '';
        spec.run(container, () => renderReviewHub(container, onBack));
      },
    });
    card.id = spec.id;
    // legacy class 保留供既有共用樣式 / 慣例相容;外觀由 .ef-card 覆蓋。
    card.classList.add('review-hub-card');
    cards.appendChild(card);
  }
}
